(function () {
  // v2: "Find a recipe" title autocomplete injected into the filter panel,
  // matching the blog search page. Facets unchanged.
  const FACET_FIELDS = ['category', 'vanilla_form', 'difficulty', 'diet', 'occasion'];

  function debounce(fn, delay) {
    let timer = null;
    return function debounced() {
      const args = arguments;
      const context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  function escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  function displayTitle(value) {
    return String(value || '').split(' | ')[0];
  }

  function normalizePath(path) {
    if (!path) return '';
    const withSlash = path.startsWith('/') ? path : '/' + path;
    return withSlash.endsWith('/') ? withSlash.slice(0, -1) : withSlash;
  }

  function quoteFilterValue(value) {
    const escaped = String(value || '').replaceAll('`', '\\`').trim();
    return '`' + escaped + '`';
  }

  function slugToLabel(value) {
    return String(value || '')
      .replaceAll('_', ' ')
      .replaceAll('-', ' ')
      .replace(/\b\w/g, function (m) {
        return m.toUpperCase();
      });
  }

  function buildTypesenseSearchUrl(config) {
    const protocol = (config.protocol || 'https').trim();
    const host = (config.host || '').trim();
    const port = (config.port || '').trim();
    const path = normalizePath((config.path || '').trim());
    const collection = (config.collection || '').trim();
    if (!host || !collection) return '';

    const portSegment = port ? ':' + port : '';
    return (
      protocol +
      '://' +
      host +
      portSegment +
      path +
      '/collections/' +
      encodeURIComponent(collection) +
      '/documents/search'
    );
  }

  function parseFacetCounts(facetCounts) {
    const facetMap = {};
    if (!Array.isArray(facetCounts)) return facetMap;

    facetCounts.forEach(function (facetEntry) {
      const fieldName = facetEntry.field_name;
      if (!fieldName || !Array.isArray(facetEntry.counts)) return;
      facetMap[fieldName] = facetEntry.counts.map(function (item) {
        return {
          value: item.value,
          count: Number(item.count || 0)
        };
      });
    });
    return facetMap;
  }

  function getSelectedSet(root, field) {
    if (!root._recipesSearchState.selectedFacets[field]) {
      root._recipesSearchState.selectedFacets[field] = new Set();
    }
    return root._recipesSearchState.selectedFacets[field];
  }

  function buildTitleJump(root) {
    const filters = root.querySelector('.recipes-search__filters');
    if (!filters) return;

    const wrap = document.createElement('div');
    wrap.className = 'recipes-search__titlejump';
    const inputId = 'vg-recipejump-' + (root.dataset.sectionId || 'x');
    wrap.innerHTML =
      '<label class="recipes-search__titlejump-label" for="' + inputId + '">Find a recipe</label>' +
      '<div class="recipes-search__titlejump-box">' +
      '<input type="text" id="' + inputId + '" class="recipes-search__titlejump-input" placeholder="Start typing a recipe name\u2026" autocomplete="off">' +
      '<ul class="recipes-search__titlejump-list" hidden></ul>' +
      '</div>';
    filters.insertBefore(wrap, filters.firstChild);

    const input = wrap.querySelector('input');
    const list = wrap.querySelector('ul');

    const lookup = debounce(async function () {
      const q = input.value.trim();
      if (q.length < 2) {
        list.hidden = true;
        list.innerHTML = '';
        return;
      }
      const state = root._recipesSearchState;
      const endpoint = buildTypesenseSearchUrl(state.config);
      if (!endpoint) return;
      const qs = new URLSearchParams({
        q: q,
        query_by: 'title',
        per_page: '8',
        page: '1'
      }).toString();
      try {
        const response = await fetch(endpoint + '?' + qs, {
          headers: { 'X-TYPESENSE-API-KEY': state.config.searchKey }
        });
        if (!response.ok) throw new Error('title lookup failed: ' + response.status);
        const payload = await response.json();
        const hits = Array.isArray(payload.hits) ? payload.hits : [];
        if (!hits.length) {
          list.innerHTML = '<li class="recipes-search__titlejump-empty">No matching recipes</li>';
          list.hidden = false;
          return;
        }
        list.innerHTML = hits
          .map(function (h) {
            const doc = h.document || {};
            return (
              '<li><a href="' +
              escapeHtml(doc.url || '#') +
              '">' +
              escapeHtml(displayTitle(doc.title)) +
              '</a></li>'
            );
          })
          .join('');
        list.hidden = false;
      } catch (error) {
        list.hidden = true;
        console.error(error);
      }
    }, 200);

    input.addEventListener('input', lookup);
    input.addEventListener('focus', function () {
      if (list.innerHTML) list.hidden = false;
    });
    document.addEventListener('click', function (event) {
      if (!wrap.contains(event.target)) list.hidden = true;
    });
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') list.hidden = true;
    });
  }

  function renderFacet(root, field, facetValues) {
    const container = root.querySelector('[data-recipes-facet="' + field + '"]');
    if (!container) return;

    const selected = getSelectedSet(root, field);
    const merged = new Map();

    (facetValues || []).forEach(function (entry) {
      merged.set(String(entry.value), Number(entry.count || 0));
    });
    selected.forEach(function (value) {
      if (!merged.has(value)) {
        merged.set(value, 0);
      }
    });

    const items = Array.from(merged.entries()).sort(function (a, b) {
      if (a[0] === b[0]) return 0;
      return a[0] < b[0] ? -1 : 1;
    });

    if (items.length === 0) {
      container.innerHTML = '<p class="recipes-search__facet-empty">No values</p>';
      return;
    }

    const html = items
      .map(function (pair, index) {
        const value = pair[0];
        const count = pair[1];
        const checked = selected.has(value) ? ' checked' : '';
        const id = 'facet-' + root.dataset.sectionId + '-' + field + '-' + index;
        return (
          '<div class="recipes-search__facet-item">' +
          '<label for="' +
          id +
          '">' +
          '<input type="checkbox" data-recipes-facet-input data-field="' +
          escapeHtml(field) +
          '" value="' +
          escapeHtml(value) +
          '"' +
          checked +
          ' id="' +
          id +
          '">' +
          '<span>' +
          escapeHtml(slugToLabel(value)) +
          '</span>' +
          '</label>' +
          '<span class="recipes-search__facet-count">' +
          escapeHtml(count) +
          '</span>' +
          '</div>'
        );
      })
      .join('');

    container.innerHTML = '<div class="recipes-search__facet-items">' + html + '</div>';
  }

  function buildFilterBy(root) {
    const state = root._recipesSearchState;
    const filters = [];

    FACET_FIELDS.forEach(function (field) {
      const selected = getSelectedSet(root, field);
      if (selected.size === 0) return;

      const values = Array.from(selected).map(quoteFilterValue);
      if (values.length === 1) {
        filters.push(field + ':=' + values[0]);
      } else {
        filters.push(field + ':=[' + values.join(',') + ']');
      }
    });

    if (state.timeFilter) {
      if (state.timeFilter.startsWith('<')) {
        filters.push('total_minutes:<' + state.timeFilter.slice(1));
      } else if (state.timeFilter.startsWith('>=')) {
        filters.push('total_minutes:>=' + state.timeFilter.slice(2));
      }
    }

    return filters.join(' && ');
  }

  function serializeQueryParams(params) {
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(function (entry) {
      const key = entry[0];
      const value = entry[1];
      if (value === undefined || value === null || value === '') return;
      urlParams.set(key, String(value));
    });
    return urlParams.toString();
  }

  function setStatus(root, status) {
    const loadingEl = root.querySelector('[data-recipes-loading]');
    const errorEl = root.querySelector('[data-recipes-error]');
    const emptyEl = root.querySelector('[data-recipes-empty]');

    loadingEl.hidden = status !== 'loading';
    errorEl.hidden = status !== 'error';
    emptyEl.hidden = status !== 'empty';
  }

  function renderCards(root, hits) {
    const results = root.querySelector('[data-recipes-results]');
    if (!results) return;

    const cards = (hits || [])
      .map(function (hitWrapper) {
        const doc = hitWrapper.document || {};
        const url = doc.url || '#';
        const title = displayTitle(doc.title || 'Untitled recipe');
        const summary = doc.summary || '';
        const imageUrl = doc.image_url || '';
        const imageAlt = doc.image_alt || title;

        const chips = [];
        if (doc.category) chips.push(slugToLabel(doc.category));
        if (doc.vanilla_form) chips.push(slugToLabel(doc.vanilla_form));
        if (doc.total_minutes) chips.push(String(doc.total_minutes) + ' min');

        const imageHtml = imageUrl
          ? '<div class="recipes-search__card-image-wrap"><img class="recipes-search__card-image" src="' +
            escapeHtml(imageUrl) +
            '" alt="' +
            escapeHtml(imageAlt) +
            '" loading="lazy"></div>'
          : '';

        const chipHtml =
          chips.length > 0
            ? '<div class="recipes-search__card-meta">' +
              chips
                .map(function (chip) {
                  return '<span class="recipes-search__chip">' + escapeHtml(chip) + '</span>';
                })
                .join('') +
              '</div>'
            : '';

        return (
          '<article class="recipes-search__card">' +
          '<a class="recipes-search__card-link" href="' +
          escapeHtml(url) +
          '">' +
          imageHtml +
          '<div class="recipes-search__card-body">' +
          '<h3 class="recipes-search__card-title">' +
          escapeHtml(title) +
          '</h3>' +
          (summary
            ? '<p class="recipes-search__card-summary">' + escapeHtml(summary) + '</p>'
            : '') +
          chipHtml +
          '</div>' +
          '</a>' +
          '</article>'
        );
      })
      .join('');

    results.innerHTML = cards;
  }

  function renderMeta(root, payload) {
    const summaryEl = root.querySelector('[data-recipes-summary]');
    const paginationEl = root.querySelector('[data-recipes-pagination]');
    const pageLabelEl = root.querySelector('[data-recipes-page-label]');
    const prevBtn = root.querySelector('[data-recipes-prev]');
    const nextBtn = root.querySelector('[data-recipes-next]');

    if (!summaryEl || !paginationEl || !pageLabelEl || !prevBtn || !nextBtn) return;

    const found = Number(payload.found || 0);
    const perPage = Number(payload.request_params && payload.request_params.per_page) || root._recipesSearchState.perPage;
    const page = Number(payload.page || 1);
    const totalPages = Math.max(1, Math.ceil(found / perPage));

    summaryEl.textContent = found.toLocaleString() + ' recipe' + (found === 1 ? '' : 's');
    pageLabelEl.textContent = 'Page ' + page + ' of ' + totalPages;

    paginationEl.hidden = totalPages <= 1;
    prevBtn.disabled = page <= 1;
    nextBtn.disabled = page >= totalPages;

    root._recipesSearchState.page = page;
    root._recipesSearchState.totalPages = totalPages;
  }

  async function runSearch(root) {
    const state = root._recipesSearchState;
    const endpoint = buildTypesenseSearchUrl(state.config);
    if (!endpoint || !state.config.searchKey || !state.config.queryBy) return;

    setStatus(root, 'loading');

    const query = state.query.trim() || '*';
    const filterBy = buildFilterBy(root);
    const params = {
      q: query,
      query_by: state.config.queryBy,
      filter_by: filterBy,
      facet_by: FACET_FIELDS.join(','),
      max_facet_values: 100,
      per_page: state.perPage,
      page: state.page
    };
    if (state.sortBy) params.sort_by = state.sortBy;

    const url = endpoint + '?' + serializeQueryParams(params);

    try {
      const response = await fetch(url, {
        headers: {
          'X-TYPESENSE-API-KEY': state.config.searchKey
        }
      });

      if (!response.ok) {
        throw new Error('Typesense request failed with status ' + response.status);
      }

      const payload = await response.json();
      const facetMap = parseFacetCounts(payload.facet_counts);
      FACET_FIELDS.forEach(function (field) {
        renderFacet(root, field, facetMap[field] || []);
      });

      const hits = Array.isArray(payload.hits) ? payload.hits : [];
      renderCards(root, hits);
      renderMeta(root, payload);

      if (hits.length === 0) {
        setStatus(root, 'empty');
      } else {
        setStatus(root, 'ok');
      }
    } catch (error) {
      setStatus(root, 'error');
      const errorEl = root.querySelector('[data-recipes-error]');
      if (errorEl) {
        errorEl.textContent = 'Search is unavailable right now. Check Typesense settings.';
      }
      console.error(error);
    }
  }

  function bindFacetChange(root) {
    root.addEventListener('change', function (event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (target.matches('[data-recipes-facet-input]')) {
        const field = target.getAttribute('data-field');
        const value = target.getAttribute('value');
        if (!field || !value) return;

        const selected = getSelectedSet(root, field);
        if (target.checked) {
          selected.add(value);
        } else {
          selected.delete(value);
        }

        root._recipesSearchState.page = 1;
        runSearch(root);
      }
    });
  }

  function init(root) {
    const host = root.dataset.typesenseHost || '';
    const collection = root.dataset.typesenseCollection || '';
    const searchKey = root.dataset.typesenseSearchKey || '';
    const queryBy = root.dataset.queryBy || '';
    if (!host || !collection || !searchKey || !queryBy) {
      const summary = root.querySelector('[data-recipes-summary]');
      if (summary) {
        summary.textContent = 'Set host, collection, key, and query fields in section settings.';
      }
      return;
    }

    root._recipesSearchState = {
      config: {
        protocol: root.dataset.typesenseProtocol || 'https',
        host: host,
        port: root.dataset.typesensePort || '',
        path: root.dataset.typesensePath || '',
        collection: collection,
        searchKey: searchKey,
        queryBy: queryBy
      },
      perPage: Number(root.dataset.perPage || 12),
      query: '',
      sortBy: '',
      timeFilter: '',
      page: 1,
      totalPages: 1,
      selectedFacets: {}
    };

    buildTitleJump(root);

    const input = root.querySelector('[data-recipes-search-input]');
    const sort = root.querySelector('[data-recipes-sort]');
    const time = root.querySelector('[data-recipes-time-filter]');
    const clear = root.querySelector('[data-recipes-clear]');
    const prev = root.querySelector('[data-recipes-prev]');
    const next = root.querySelector('[data-recipes-next]');

    if (input) {
      input.addEventListener(
        'input',
        debounce(function () {
          root._recipesSearchState.query = input.value || '';
          root._recipesSearchState.page = 1;
          runSearch(root);
        }, 220)
      );
    }

    if (sort) {
      sort.addEventListener('change', function () {
        root._recipesSearchState.sortBy = sort.value || '';
        root._recipesSearchState.page = 1;
        runSearch(root);
      });
    }

    if (time) {
      time.addEventListener('change', function () {
        root._recipesSearchState.timeFilter = time.value || '';
        root._recipesSearchState.page = 1;
        runSearch(root);
      });
    }

    if (clear) {
      clear.addEventListener('click', function () {
        root._recipesSearchState.query = '';
        root._recipesSearchState.sortBy = '';
        root._recipesSearchState.timeFilter = '';
        root._recipesSearchState.page = 1;
        root._recipesSearchState.selectedFacets = {};

        if (input) input.value = '';
        if (sort) sort.value = '';
        if (time) time.value = '';

        const jumpInput = root.querySelector('.recipes-search__titlejump-input');
        const jumpList = root.querySelector('.recipes-search__titlejump-list');
        if (jumpInput) jumpInput.value = '';
        if (jumpList) { jumpList.innerHTML = ''; jumpList.hidden = true; }

        runSearch(root);
      });
    }

    if (prev) {
      prev.addEventListener('click', function () {
        const state = root._recipesSearchState;
        if (state.page <= 1) return;
        state.page = state.page - 1;
        runSearch(root);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        const state = root._recipesSearchState;
        if (state.page >= state.totalPages) return;
        state.page = state.page + 1;
        runSearch(root);
      });
    }

    bindFacetChange(root);
    runSearch(root);
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-recipes-search-root]').forEach(init);
  });
})();